import React, { useState, useEffect, useCallback } from 'react';
import { agentApi, merchantApi, tshareUrl } from '../../services/api';
import { PrimaryBtn, GhostBtn, EmptyState } from './AdminLayout';

// 人工活动：有的商家不碰商家 App，全靠运营人工服务（2026-10-01 需求）。
//
// 一条活动从头到尾：
//   新建（替商家填条件）→ 博主在 App「公开招募」报名 → 复制候选名单给商家线下挑
//   → 回来勾上「确认选中」→ 助理自动去约时间 → 复制确认文案给商家
//   → 交稿走人工审稿（在「审稿」栏）→ 发布、回填 → 代商家打分
//
// 名额是这家店的「人工名额」，跟 App 套餐名额分开算；选中时查、约成时扣。

const WD = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
const NUM = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟'];
const num = (i) => NUM[i] || `${i + 1}.`;
// 跟老后台复制出来的格式一样：8月22日 周五 12:00
const fmtVisit = (v) => {
  if (!v) return '时间待定';
  const d = new Date(v);
  return `${d.getMonth() + 1}月${d.getDate()}日 ${WD[d.getDay()]} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
// 今天 / 明天 / 后天：约好的人一眼看出谁先来
const dayTag = (v) => {
  if (!v) return '';
  const a = new Date(); a.setHours(0, 0, 0, 0);
  const b = new Date(v); b.setHours(0, 0, 0, 0);
  return ['今天', '明天', '后天'][Math.round((b - a) / 86400e3)] || '';
};
const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('zh-CN') : '');

// 复制给商家的文案：沿用老后台那一种格式（选人、确认时间都用它，用户 2026-10-01 定）——
// 1️⃣名字 电话 时间 N人 备注 / （空行）小红书：账号 / 主页链接 / （空行）粉丝：N
// 还没定时间的写「时间待定」
function copyLine(r, i) {
  const b = r.blogger;
  const head = [`${num(i)}${b.name || '博主'}`, b.contactPhone || '', r.visitAt ? fmtVisit(r.visitAt) : '时间待定', `${r.peopleCount || 1}人`, (r.note || '').trim()]
    .filter(Boolean).join(' ');
  const lines = [head, '', `小红书：${b.xhsAccount || ''}`.trim()];
  if (b.xhsUrl) lines.push(/^https?:\/\//i.test(b.xhsUrl) ? b.xhsUrl : `https://${b.xhsUrl}`);
  lines.push('', `粉丝：${b.fans || 0}`);
  return lines.join('\n');
}
const candidateText = (rows) => rows.map(copyLine).join('\n\n');
const confirmText = (rows) => rows.map(copyLine).join('\n\n');
// 完整报告：做完的每人一条帖子链接
function reportText(act, rows) {
  const body = rows.map((r, i) => {
    const links = (r.draft?.postLinks || []).map((p) => `${p.platform || '帖子'}：${p.url || '（截图）'}`);
    return [`${num(i)}${r.blogger.name || '博主'} ${fmtVisit(r.visitAt)}`, ...links].join('\n');
  }).join('\n\n');
  return `${act.merchantName} · ${act.name}\n\n${body}`;
}

const copy = async (text, setMsg, what) => {
  try { await navigator.clipboard.writeText(text); setMsg(`${what}已复制`); }
  catch { window.prompt('复制下面这段', text); }
};

const STAGE = { recruiting: '招募中', selecting: '挑人中', running: '进行中', completed: '已结束' };

export default function ManualActivities({ setMsg, openChat, openDeal }) {
  const [list, setList] = useState([]);
  const [actId, setActId] = useState('');
  const [creating, setCreating] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(() => {
    agentApi.manualActs(showClosed ? {} : { open: '1' }).then(setList).catch(() => setList([]));
  }, [showClosed]);
  useEffect(() => { load(); }, [load]);

  if (creating) return <CreateForm setMsg={setMsg} onDone={(id) => { setCreating(false); load(); if (id) setActId(id); }} />;
  if (actId) {
    return <ActDetail id={actId} setMsg={setMsg} openChat={openChat} openDeal={openDeal}
      onBack={() => { setActId(''); load(); }} />;
  }
  return (
    <>
      <div style={s.warn}>
        给<b>不用商家 App</b> 的商家用：运营替他建活动、博主在 App「公开招募」报名、你们拿名单给商家线下挑。
        挑中之后助理会自动去约时间；稿子要人工审（在「审稿」栏）。名额用的是这家店的<b>人工名额</b>，跟 App 套餐名额分开。
      </div>
      <div style={s.actionRow}>
        <PrimaryBtn small onClick={() => setCreating(true)}>新建人工活动</PrimaryBtn>
        <span style={{ ...s.chip, ...(showClosed ? s.chipOn : {}) }} onClick={() => setShowClosed(!showClosed)}>
          {showClosed ? '包括已结束的' : '只看进行中的'}
        </span>
      </div>
      {!list.length ? <EmptyState text="还没有人工活动" /> : list.map((a) => (
        <div key={a._id} style={s.card} onClick={() => setActId(a._id)}>
          <div style={s.cardTop}>
            <span style={s.name}>{a.merchantName}</span>
            <span style={s.sub}>{a.name}</span>
            <span style={s.tag}>{STAGE[a.stage] || a.stage}</span>
          </div>
          <div style={s.line}>
            招 {a.neededCount || '—'} 位 · 报名 {a.summary.applied} · 在约 {a.summary.talking} · 约好 {a.summary.booked} · 完成 {a.summary.done}
            {' '}· 人工名额还能选 {a.openManualQuota}
            {a.applyDeadline ? ` · ${fmtDate(a.applyDeadline)} 截止报名` : ''}
          </div>
        </div>
      ))}
    </>
  );
}

// ── 新建：选商家 + 活动信息 + 替商家填条件 ──
function CreateForm({ setMsg, onDone }) {
  const [merchants, setMerchants] = useState([]);
  const [q, setQ] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [f, setF] = useState({
    name: '', intro: '', neededCount: 3, applyDeadline: '',
    mealValue: 120, peopleAllowed: 2, paidFee: 0, postCount: 1, deadlineDays: 7,
    platforms: '小红书', mustTags: '', mustShoot: '', highlights: '', storeAddress: '',
    minWords: 150, minImages: 6,
  });
  const [slots, setSlots] = useState([{ weekday: 5, from: '11:00', to: '14:00' }]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { merchantApi.list().then((r) => setMerchants(Array.isArray(r) ? r : (r.list || []))).catch(() => {}); }, []);
  // 选了店就把他现有的条件带出来，改一改就行
  useEffect(() => {
    if (!merchantId) return;
    agentApi.terms(merchantId).then(({ terms: t }) => {
      if (!t) return;
      setF((x) => ({
        ...x,
        mealValue: t.mealValue ?? x.mealValue, peopleAllowed: t.peopleAllowed ?? x.peopleAllowed,
        paidFee: t.paidFee ?? x.paidFee, postCount: t.postCount ?? x.postCount, deadlineDays: t.deadlineDays ?? x.deadlineDays,
        platforms: (t.platforms || []).join('、') || x.platforms, mustTags: (t.mustTags || []).join('、'),
        mustShoot: (t.mustShoot || []).join('、'), highlights: t.highlights || '', storeAddress: t.storeAddress || '',
        minWords: t.minWords || x.minWords, minImages: t.minImages || x.minImages,
      }));
      if ((t.availability || []).length) setSlots(t.availability.map((a) => ({ weekday: a.weekday, from: a.from, to: a.to })));
    }).catch(() => {});
  }, [merchantId]);

  const shown = merchants.filter((m) => !q || String(m.name || '').includes(q)).slice(0, 30);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const split = (v) => String(v || '').split(/[、,，\s]+/).map((x) => x.trim()).filter(Boolean);

  const save = async () => {
    if (!merchantId) { setMsg('先选商家'); return; }
    if (!slots.length) { setMsg('至少填一个可接待时段，助理要照着约时间'); return; }
    setBusy(true);
    try {
      await agentApi.saveTerms(merchantId, {
        mealValue: Number(f.mealValue) || 0, peopleAllowed: Number(f.peopleAllowed) || 1,
        neededCount: Number(f.neededCount) || 0, paidFee: Number(f.paidFee) || 0,
        postCount: Number(f.postCount) || 1, deadlineDays: Number(f.deadlineDays) || 7,
        platforms: split(f.platforms), mustTags: split(f.mustTags), mustShoot: split(f.mustShoot),
        highlights: f.highlights, storeAddress: f.storeAddress,
        minWords: Number(f.minWords) || 150, minImages: Number(f.minImages) || 6,
        availability: slots.map((x) => ({ weekday: Number(x.weekday), from: x.from, to: x.to })),
      });
      const act = await agentApi.createManualAct({
        merchantId, name: f.name, intro: f.intro, neededCount: Number(f.neededCount) || 0,
        applyDeadline: f.applyDeadline || null,
      });
      setMsg('活动建好了，博主现在能在 App「公开招募」里看到');
      onDone(act._id);
    } catch (e) { setMsg(e?.response?.data?.error || '没建成'); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div style={s.actionRow}><GhostBtn small onClick={() => onDone('')}>← 返回</GhostBtn></div>
      <div style={s.panel}>
        <div style={s.panelTitle}>哪家店</div>
        <input style={s.input} placeholder="搜店名" value={q} onChange={(e) => setQ(e.target.value)} />
        <div style={s.pickList}>
          {shown.map((m) => (
            <span key={m._id} style={{ ...s.chip, ...(merchantId === m._id ? s.chipOn : {}) }} onClick={() => setMerchantId(m._id)}>{m.name}</span>
          ))}
        </div>
      </div>

      <div style={s.panel}>
        <div style={s.panelTitle}>活动</div>
        <Field k="活动名" v={<input style={s.input} value={f.name} onChange={set('name')} placeholder="不填就叫「店名 探店招募」" />} />
        <Field k="介绍" v={<textarea style={s.area} value={f.intro} onChange={set('intro')} placeholder="给博主看的：店里有什么、想找什么样的人" />} />
        <Field k="招几位" v={<input style={s.inputS} type="number" value={f.neededCount} onChange={set('neededCount')} />} />
        <Field k="报名截止" v={<input style={s.inputS} type="date" value={f.applyDeadline} onChange={set('applyDeadline')} />} />
      </div>

      <div style={s.panel}>
        <div style={s.panelTitle}>商家条件 <span style={s.sub}>替商家填，助理约时间、机器查稿都照这个</span></div>
        <Field k="套餐 A$" v={<input style={s.inputS} type="number" value={f.mealValue} onChange={set('mealValue')} />} />
        <Field k="最多几人" v={<input style={s.inputS} type="number" value={f.peopleAllowed} onChange={set('peopleAllowed')} />} />
        <Field k="现金 A$" v={<input style={s.inputS} type="number" value={f.paidFee} onChange={set('paidFee')} />} />
        <Field k="可接待" v={(
          <div>
            {slots.map((x, i) => (
              <div key={i} style={s.slotRow}>
                <select style={s.inputS} value={x.weekday} onChange={(e) => setSlots(slots.map((y, j) => (j === i ? { ...y, weekday: Number(e.target.value) } : y)))}>
                  {WD.map((w, wi) => <option key={wi} value={wi}>{w}</option>)}
                </select>
                <input style={s.inputS} value={x.from} onChange={(e) => setSlots(slots.map((y, j) => (j === i ? { ...y, from: e.target.value } : y)))} />
                <span style={s.sub}>到</span>
                <input style={s.inputS} value={x.to} onChange={(e) => setSlots(slots.map((y, j) => (j === i ? { ...y, to: e.target.value } : y)))} />
                <GhostBtn small onClick={() => setSlots(slots.filter((_, j) => j !== i))}>删掉</GhostBtn>
              </div>
            ))}
            <GhostBtn small onClick={() => setSlots([...slots, { weekday: 6, from: '11:00', to: '14:00' }])}>加一个时段</GhostBtn>
          </div>
        )} />
        <Field k="门店地址" v={<input style={s.input} value={f.storeAddress} onChange={set('storeAddress')} />} />
        <Field k="发布平台" v={<input style={s.input} value={f.platforms} onChange={set('platforms')} placeholder="用顿号隔开，如 小红书、Instagram" />} />
        <Field k="发几篇" v={<input style={s.inputS} type="number" value={f.postCount} onChange={set('postCount')} />} />
        <Field k="几天内发" v={<input style={s.inputS} type="number" value={f.deadlineDays} onChange={set('deadlineDays')} />} />
        <Field k="必拍" v={<input style={s.input} value={f.mustShoot} onChange={set('mustShoot')} placeholder="招牌菜、门头…" />} />
        <Field k="必带话题" v={<input style={s.input} value={f.mustTags} onChange={set('mustTags')} />} />
        <Field k="重点写" v={<textarea style={s.area} value={f.highlights} onChange={set('highlights')} />} />
        <Field k="字数 / 图" v={(
          <span><input style={s.inputS} type="number" value={f.minWords} onChange={set('minWords')} /> 字
            <input style={s.inputS} type="number" value={f.minImages} onChange={set('minImages')} /> 张图</span>
        )} />
      </div>
      <PrimaryBtn disabled={busy} onClick={save}>{busy ? '建着…' : '建好，开始招募'}</PrimaryBtn>
    </div>
  );
}

function Field({ k, v }) {
  return <div style={s.field}><span style={s.fieldK}>{k}</span><div style={{ flex: 1 }}>{v}</div></div>;
}

// ── 一个活动：报名的人 / 推荐的人 / 在约 / 约好 / 完成 ──
function ActDetail({ id, setMsg, openChat, openDeal, onBack }) {
  const [d, setD] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState('');
  const load = useCallback(() => { agentApi.manualAct(id).then(setD).catch((e) => setMsg(e?.response?.data?.error || '打不开')); }, [id, setMsg]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <EmptyState text="加载中…" />;

  const act = d.activity;
  const applied = d.deals.filter((x) => x.status === 'applied');
  const talking = d.deals.filter((x) => x.status === 'negotiating' || x.status === 'invited');
  // 排序（用户 2026-10-01）：约好了还没去的排最前、越早要去的越靠前；在约的其次；报名的再往后；
  // 去过了的、发出来了的排最后。「去过了」= 博主说了到店，或者约的时间已经过了 3 小时
  const now = Date.now();
  const wentAlready = (x) => x.arrived || (x.visitAt && new Date(x.visitAt).getTime() < now - 3 * 3600e3);
  const isBooked = (x) => (x.status === 'agreed' || x.status === 'committed') && !x.publishedAt;
  const byVisit = (a, b) => (a.visitAt ? new Date(a.visitAt).getTime() : Infinity) - (b.visitAt ? new Date(b.visitAt).getTime() : Infinity);
  const upcoming = d.deals.filter((x) => isBooked(x) && !wentAlready(x)).sort(byVisit);
  const visited = d.deals.filter((x) => isBooked(x) && wentAlready(x)).sort(byVisit);
  const done = d.deals.filter((x) => !!x.publishedAt);
  // 复制今天 / 未来 7 天要来的：给商家提前提醒
  const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
  const within = (days) => upcoming.filter((x) => x.visitAt && new Date(x.visitAt) < new Date(dayStart.getTime() + days * 86400e3));
  const today = within(1);
  const week = within(7);
  const ended = d.deals.filter((x) => ['not_selected', 'declined', 'expired'].includes(x.status));
  const toggle = (phone) => { const n = new Set(picked); n.has(phone) ? n.delete(phone) : n.add(phone); setPicked(n); };
  const pickedRows = (rows) => rows.filter((r) => picked.has(r.blogger.phone));

  const select = async () => {
    const phones = [...picked];
    if (!phones.length) { setMsg('先勾商家挑中的人'); return; }
    if (!window.confirm(`确认选中 ${phones.length} 位？选中后助理会马上给他们发消息约时间。`)) return;
    setBusy('select');
    try { const r = await agentApi.selectManual(id, phones); setMsg(`选中 ${r.selected} 位，助理已经去约时间了`); setPicked(new Set()); load(); }
    catch (e) { setMsg(e?.response?.data?.error || '没选成'); }
    finally { setBusy(''); }
  };
  const nudge = async () => {
    const phones = d.recommended.filter((r) => picked.has(r.phone)).map((r) => r.phone);
    if (!phones.length) { setMsg('先在推荐名单里勾人'); return; }
    setBusy('nudge');
    try { const r = await agentApi.nudgeManual(id, phones); setMsg(`已通知 ${r.sent} 位去报名`); }
    catch (e) { setMsg(e?.response?.data?.error || '没发出去'); }
    finally { setBusy(''); }
  };
  const addQuota = async () => {
    const n = Number(window.prompt('人工名额加几位？（减就填负数）', '1'));
    if (!n) return;
    const note = window.prompt('备注（比如：线下收了 A$300）', '') || '';
    try { await agentApi.manualQuota(act.merchantId, n, note); load(); setMsg('人工名额已更新'); }
    catch (e) { setMsg(e?.response?.data?.error || '没改成'); }
  };
  const close = async () => {
    if (!window.confirm('结束这个活动？不再收报名；还没被挑的人会变成「未选中」（不通知他们）。')) return;
    try { await agentApi.closeManualAct(id); load(); setMsg('活动已结束'); }
    catch (e) { setMsg(e?.response?.data?.error || '没结束成'); }
  };
  const rate = async (r) => {
    const ask = (k) => Number(window.prompt(`${k}（1-5）`, '5')) || 0;
    const onTime = ask('准时'); if (!onTime) return;
    const content = ask('内容'); const cooperation = ask('配合');
    const comment = window.prompt('商家评语（可空，只留在这单上，不进画像）', '') || '';
    try { await agentApi.rateDeal(r._id, { onTime, content, cooperation, comment }); load(); setMsg('打好分了'); }
    catch (e) { setMsg(e?.response?.data?.error || '没打成'); }
  };

  const bookedBtns = (r) => (
          <div style={s.btns}>
            {r.conversationId && <GhostBtn small onClick={() => openChat(r.conversationId, 'manual', `${r.blogger.name} · 博主`)}>看对话</GhostBtn>}
            <GhostBtn small onClick={() => openDeal(r._id, 'manual')}>看这单</GhostBtn>
            {r.draft && <span style={s.sub}>稿子：{DRAFT_ST[r.draft.status] || r.draft.status}</span>}
            {r.draft && r.draft.status === 'ops_review' && (
              <GhostBtn small onClick={() => copy(tshareUrl(act.shareToken, { view: 'drafts', app: r._id }), setMsg, '审稿链接')}>复制审稿链接给商家</GhostBtn>
            )}
          </div>
          );
  const Person = ({ r, withCheck, extra }) => {
    const b = r.blogger || r;
    return (
      <div style={s.person}>
        {withCheck && <input type="checkbox" checked={picked.has(b.phone)} onChange={() => toggle(b.phone)} />}
        <div style={{ flex: 1 }}>
          <div style={s.cardTop}>
            <span style={s.name}>{b.name || b.phone}</span>
            {b.score > 0 && <span style={s.score}>{b.score} 分</span>}
            <span style={s.sub}>{b.xhsAccount || '没填小红书'} · {b.fans} 粉</span>
            {b.xhsUrl && <a style={s.link} href={b.xhsUrl} target="_blank" rel="noreferrer">主页</a>}
            {b.noDispatch && <span style={s.tagWarn}>不派单</span>}
          </div>
          {r.status && <div style={s.line}>{r.peopleCount || 1} 人{r.note ? ` · ${r.note}` : ''}{r.visitAt ? ` · ${fmtVisit(r.visitAt)}` : ''}
            {r.visitAt && !r.publishedAt && !r.arrived && dayTag(r.visitAt) && <span style={{ ...s.tag, marginLeft: 6 }}>{dayTag(r.visitAt)}</span>}</div>}
          {b.bio && <div style={s.bio}>{b.bio}</div>}
          {(b.reasons || []).length > 0 && <div style={s.reasons}>{b.reasons.slice(0, 3).join('；')}</div>}
          {extra}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={s.actionRow}>
        <GhostBtn small onClick={onBack}>← 所有人工活动</GhostBtn>
        {act.stage !== 'completed' && <GhostBtn small onClick={close}>结束活动</GhostBtn>}
      </div>
      <div style={s.panel}>
        <div style={s.cardTop}><span style={s.name}>{act.merchantName}</span><span style={s.sub}>{act.name}</span><span style={s.tag}>{STAGE[act.stage]}</span></div>
        <div style={s.line}>
          招 {act.neededCount || '—'} 位 · 人工名额余额 {d.merchant?.tandianBalance || 0}，还能选 {d.openManualQuota} 位
          {act.applyDeadline ? ` · ${fmtDate(act.applyDeadline)} 截止报名` : ''}
          {' '}<span style={s.link} onClick={addQuota}>加人工名额</span>
        </div>
        {d.terms && (
          <div style={s.line}>
            套餐 A${d.terms.mealValue || 0} · 最多 {d.terms.peopleAllowed || 1} 人 ·
            {' '}{(d.terms.availability || []).map((x) => `${WD[x.weekday]} ${x.from}-${x.to}`).join('、') || '没填接待时段'}
          </div>
        )}
      </div>

      {upcoming.length > 0 && <>
        <div style={s.section}>约好了，还没去（{upcoming.length}）· 越早的越靠前</div>
        <div style={s.actionRow}>
          <GhostBtn small disabled={!today.length} onClick={() => copy(confirmText(today), setMsg, '今天要来的')}>复制今天要来的（{today.length}）</GhostBtn>
          <GhostBtn small disabled={!week.length} onClick={() => copy(confirmText(week), setMsg, '未来 7 天要来的')}>复制未来 7 天要来的（{week.length}）</GhostBtn>
          <GhostBtn small onClick={() => copy(confirmText(upcoming), setMsg, '确认文案')}>复制全部确认文案</GhostBtn>
          <span style={s.sub}>商家说时间不行：进「看对话」用助理身份说一句，或让商家说新时间</span>
        </div>
        {upcoming.map((r) => <Person key={r._id} r={r} extra={bookedBtns(r)} />)}
      </>}

      {talking.length > 0 && <>
        <div style={s.section}>助理在约时间（{talking.length}）</div>
        {talking.map((r) => <Person key={r._id} r={r} extra={(
          <div style={s.btns}>
            {r.conversationId && <GhostBtn small onClick={() => openChat(r.conversationId, 'manual', `${r.blogger.name} · 博主`)}>看对话</GhostBtn>}
            <GhostBtn small onClick={() => openDeal(r._id, 'manual')}>看这单</GhostBtn>
          </div>
        )} />)}
      </>}

      <div style={s.section}>报名的人（{applied.length}）</div>
      <div style={s.actionRow}>
        <GhostBtn small onClick={() => copy(candidateText(pickedRows(applied).length ? pickedRows(applied) : applied), setMsg, '候选名单')}>
          复制候选名单{pickedRows(applied).length ? `（已勾 ${pickedRows(applied).length}）` : '（全部）'}
        </GhostBtn>
        {/* 老后台那种选人链接：商家打开网页自己勾，勾完助理去约时间（跟「确认选中」同一套） */}
        <GhostBtn small onClick={() => copy(tshareUrl(act.shareToken, { view: 'select' }), setMsg, '选人链接')}>复制选人链接给商家</GhostBtn>
        <PrimaryBtn small disabled={busy === 'select'} onClick={select}>{busy === 'select' ? '选着…' : `确认选中（${picked.size}）`}</PrimaryBtn>
      </div>
      {!applied.length ? <div style={s.empty}>还没人报名。可以从下面的推荐里请人来报名。</div>
        : applied.map((r) => <Person key={r._id} r={r} withCheck />)}

      <div style={s.section}>系统推荐（没报名，但分高）</div>
      <div style={s.actionRow}>
        <GhostBtn small disabled={busy === 'nudge'} onClick={nudge}>邀请勾上的人来报名</GhostBtn>
        <span style={s.sub}>商家直接从这里挑中的，勾上后点上面的「确认选中」也行</span>
      </div>
      {!d.recommended.length ? <div style={s.empty}>没有推荐的人</div>
        : d.recommended.map((b) => <Person key={b.phone} r={{ blogger: b }} withCheck />)}

      {visited.length > 0 && <>
        <div style={s.section}>去过了，等稿子 / 等发布（{visited.length}）</div>
        {visited.map((r) => <Person key={r._id} r={r} extra={bookedBtns(r)} />)}
      </>}

      {done.length > 0 && <>
        <div style={s.section}>发出来了（{done.length}）</div>
        <div style={s.actionRow}>
          <GhostBtn small onClick={() => copy(tshareUrl(act.shareToken, { view: 'report' }), setMsg, '报告链接')}>复制报告链接给商家</GhostBtn>
          <GhostBtn small onClick={() => copy(reportText(act, done), setMsg, '完整报告')}>复制完整报告（文字）</GhostBtn>
        </div>
        {done.map((r) => <Person key={r._id} r={r} extra={(
          <div style={s.btns}>
            {(r.draft?.postLinks || []).map((p, i) => p.url && <a key={i} style={s.link} href={p.url} target="_blank" rel="noreferrer">{p.platform || '帖子'}</a>)}
            {r.ratedAt ? <span style={s.sub}>已打分</span> : <PrimaryBtn small onClick={() => rate(r)}>代商家打分</PrimaryBtn>}
          </div>
        )} />)}
      </>}

      {ended.length > 0 && <>
        <div style={s.section}>没成的（{ended.length}）</div>
        {ended.map((r) => <div key={r._id} style={s.line}>{r.blogger.name} · {END_ST[r.status]}</div>)}
      </>}
    </div>
  );
}

const DRAFT_ST = { auto_failed: '机器退回，在改', ops_review: '等人工审', approved: '审过了，待发布', rejected: '打回在改', published: '已发布' };
const END_ST = { not_selected: '未选中', declined: '博主拒绝', expired: '没回应' };

const s = {
  warn: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12, padding: '10px 12px', borderRadius: 8, marginBottom: 10, lineHeight: 1.7 },
  actionRow: { display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' },
  card: { background: '#fff', borderRadius: 10, padding: '12px 14px', marginBottom: 8, border: '1px solid #f0f0f0', cursor: 'pointer' },
  panel: { background: '#fff', borderRadius: 10, padding: '12px 14px', marginBottom: 10, border: '1px solid #f0f0f0' },
  panelTitle: { fontSize: 14, fontWeight: 600, color: '#111', marginBottom: 10 },
  cardTop: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  name: { fontSize: 15, fontWeight: 600, color: '#111' },
  sub: { fontSize: 12, color: '#6b7280' },
  line: { fontSize: 13, color: '#4b5563', marginTop: 6, lineHeight: 1.6 },
  bio: { fontSize: 12, color: '#6b7280', marginTop: 4, lineHeight: 1.6 },
  reasons: { fontSize: 12, color: '#9ca3af', marginTop: 4 },
  tag: { fontSize: 11, color: '#374151', background: '#f3f4f6', padding: '1px 8px', borderRadius: 4 },
  tagWarn: { fontSize: 11, color: '#b45309', background: '#fffbeb', padding: '1px 8px', borderRadius: 4 },
  score: { fontSize: 12, fontWeight: 600, color: '#FA1C51' },
  link: { fontSize: 12, color: '#2563eb', cursor: 'pointer', textDecoration: 'none' },
  chip: { fontSize: 13, color: '#6b7280', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 999, padding: '4px 14px', cursor: 'pointer' },
  chipOn: { color: '#fff', background: '#FA1C51', borderColor: '#FA1C51' },
  pickList: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, maxHeight: 180, overflowY: 'auto' },
  section: { fontSize: 14, fontWeight: 600, color: '#374151', margin: '20px 0 8px' },
  person: { display: 'flex', gap: 10, alignItems: 'flex-start', background: '#fff', borderRadius: 10, padding: '10px 12px', marginBottom: 6, border: '1px solid #f0f0f0' },
  btns: { display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' },
  empty: { fontSize: 13, color: '#9ca3af', padding: '6px 2px' },
  field: { display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 },
  fieldK: { width: 72, flexShrink: 0, fontSize: 13, color: '#6b7280', paddingTop: 8 },
  input: { width: '100%', padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none' },
  inputS: { width: 90, padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none', marginRight: 6 },
  area: { width: '100%', minHeight: 64, padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  slotRow: { display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6, flexWrap: 'wrap' },
};
